import type { ReactNode } from "react";
import { Button } from "@noiman/ui/button";
import { usePressBurst } from "@noiman/ui/press-burst";

/** 答え合わせのあとの札。right は正解、wrong は選んだ間違い、dim は選ばれなかった札 */
export type LgChoiceResult = "right" | "wrong" | "dim";

export type LgChoiceProps = {
  children: ReactNode;
  /** 選んでいる札 (aria-pressed) */
  pressed?: boolean;
  /** 答え合わせの結果。right は緑の淡い地と枠に印と「正解」、wrong は淡い橙赤の地と枠に印と「選んだ答え」、dim は薄く */
  result?: LgChoiceResult;
  /** 答えたあとは押しても何も起きない。disabled にせず aria-disabled にして、字は薄くしない */
  settled?: boolean;
  disabled?: boolean;
  /** 幅いっぱいに広げる (2 列に並べる問いの札) */
  fill?: boolean;
  /** 押した所から波紋と印を出す。小テストと確認テストの選択肢だけ true (予想などの札には付けない)。答えたあと (settled) は出さない。金は使わない (頁の黄は次へ進む所が持つ) */
  burst?: boolean;
  onClick: () => void;
  className?: string;
};

const VERDICT: Record<"right" | "wrong", string> = { right: "正解", wrong: "選んだ答え" };

/** 正誤の印。塗りの丸の中にチェックか × を抜く (partial は結果のまとめ用。全問正解でないときの、正誤を言わない輪と横線)。字の ○ や × ではなく SVG (ラジオボタンに見えない)。色は lg.css の data-kind が決める */
export function LgVerdictMark({ kind, size = 18, className }: { kind: "right" | "wrong" | "partial"; size?: number; className?: string }) {
  return (
    <svg className={`lg-verdict-mark${className ? ` ${className}` : ""}`} data-kind={kind} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle className="lg-verdict-mark__disc" cx="12" cy="12" r={kind === "partial" ? 11 : 12} />
      <path className="lg-verdict-mark__line" d={kind === "right" ? "M7 12.5l3.2 3.2L17 8.8" : kind === "partial" ? "M7.5 12h9" : "M8 8l8 8M16 8l-8 8"} fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * 選ぶ札。予想、AI に頼む文、ふり返りの問いで使う。@noiman/ui の Button を包み、色、角、線は tokens.css が決める。
 * 正解の札は緑、間違えた札は橙赤で、頭に塗りの丸の印と「正解」「選んだ答え」。色だけに頼らず、印と字でも言う
 */
export function LgChoice({ children, pressed, result, settled = false, disabled, fill = false, burst = false, onClick, className }: LgChoiceProps) {
  const press = usePressBurst("none");
  const cls = `lg-choice${fill ? " lg-choice--fill" : ""}${className ? ` ${className}` : ""}`;
  return (
    <Button
      type="button"
      variant="ghost"
      className={cls}
      disabled={disabled}
      aria-disabled={settled || undefined}
      aria-pressed={pressed}
      data-result={result === "right" || result === "wrong" ? result : undefined}
      data-dim={result === "dim" ? "true" : undefined}
      onClick={(e) => {
        if (burst && !settled) press.fire(e);
        onClick();
      }}
    >
      <span className="lg-choice__body">
        {result === "right" || result === "wrong" ? (
          <span className="lg-choice__verdict">
            <LgVerdictMark kind={result} size={16} />
            {VERDICT[result]}
          </span>
        ) : null}
        <span className="lg-choice__text">{children}</span>
      </span>
      {burst ? press.layer : null}
    </Button>
  );
}
